import React from 'react';
import { FormProvider, useFormContext } from 'react-hook-form';

import styles from './Interest.module.scss';
import Button from '../../../../components/button/button';
import FormDropdown from '../../../../components/formDropdown/FormDropdown';
import FormInput from '../../../../components/formInput/FormInput';
import Portal from '../../../../components/portal/Portal';
import StepperNavigation from '../../../../components/stepper/StepperNavigation';
import InputContainer from '../../../../components/template/InputContainer/InputContainer';
import SectionContainer from '../../../../components/template/SectionContainer/SectionContainer';
import Title from '../../../../components/template/Title/Title';
import { QuestionForm } from '../../../../models/questionModel';
import useFormStore from '../../../../store/FormStore';
import useQuestionStore from '../../../../store/QuestionStore';
import useStepperStore from '../../../../store/StepperStore';
import MainQuestion from '../MainQuestion/MainQuestion';

interface InterestProps {}

const Interest: React.FC<InterestProps> = ({}) => {
  const { setQuestion } = useQuestionStore();
  const { setStepper } = useStepperStore();
  const { setForm } = useFormStore();

  //#region Form
  const formMethods = useFormContext();
  const {
    formState: { isValid },
    getValues,
    watch,
    reset,
  } = formMethods;
  const { interest } = watch();
  //#endregion

  const InterestData = {
    title: 'Interest risk',
    description:
      'The potential for investment losses that result from a change in interest rates.',
    compulsoryQuestions: [
      {
        id: 'interest.compulsory',
        question: 'Is your Company exposed to interest risk?',
        inputs: [
          { value: 'Yes', label: 'Yes' },
          { value: 'Not applicable', label: 'Not applicable' },
        ],
      },
    ],
    probabilityQuestions: [
      {
        id: 'interest.probability',
        question: 'How frequent is the Company facing interest risk?',
        inputs: [
          { value: 1, label: 'Improbable' },
          { value: 2, label: 'Remote' },
          { value: 3, label: 'Fairly Likely' },
          { value: 4, label: 'Probable' },
          { value: 5, label: 'Very Likely' },
        ],
      },
    ],
  };

  const expectationOptions = [
    {
      label: 'Yes, even above my expectation',
      value: 1,
    },
    {
      label: 'Yes, within my expectation',
      value: 2,
    },
    {
      label: 'Neither above nor below my expectation',
      value: 3,
    },
    {
      label: 'No, below my expectation',
      value: 4,
    },
    {
      label: 'Not sure / I don’t know',
      value: 5,
    },
  ];

  const onHandleNavigation = (nav: 'next' | 'prev') => {
    if (!isValid && nav == 'next') return;

    const compulsoryQuestions = InterestData.compulsoryQuestions.map((comp) => {
      return {
        answer: getValues('interest.compulsory'),
        question: comp.question,
        questionNotes: [],
        answerNotes: [],
      };
    });

    compulsoryQuestions.push(
      ...([
        {
          answer: getValues('interest.notionalAmount'),
          question: 'Notional amount',
          questionNotes: [],
          answerNotes: [],
        },
        {
          answer: getValues('interest.swapTenor'),
          question: 'Swap tenor (years)',
          questionNotes: [],
          answerNotes: [],
        },
        {
          answer: getValues('interest.paymentFrequency'),
          question: 'Payment frequency',
          questionNotes: [],
          answerNotes: [],
        },
        {
          answer: getValues('interest.discountRate'),
          question: 'Discount Rate',
          questionNotes: [],
          answerNotes: [],
        },
        {
          answer: getValues('interest.forwardRate'),
          question: 'Forward rate (% per period, comma separated) ',
          questionNotes: [],
          answerNotes: [],
        },
      ] as any)
    );

    const finalInterestData = new QuestionForm(
      InterestData.title,
      InterestData.description,
      compulsoryQuestions,
      [
        {
          answer:
            expectationOptions.find(
              (item) => item.value == getValues('interest.expectation')
            )?.label || '',
          answerNotes: [],
          marks: +getValues('interest.expectation'),
          question: 'Is the result within your expectation?',
          questionNotes: [],
        },
      ],
      InterestData.probabilityQuestions.map((comp) => {
        return {
          answer:
            comp.inputs.find(
              (inp) => inp.value == Number(getValues('interest.probability'))
            )?.label || '',
          question: comp.question,
          questionNotes: [],
          answerNotes: [],
          marks: Number(getValues('interest.probability')),
        };
      })
    );

    setQuestion({ interest: finalInterestData });

    setForm({ ...getValues() });

    setStepper(
      nav,
      undefined,
      interest?.compulsory == '' ? undefined : interest?.compulsory == 'Yes'
    );
  };

  const generateRandomData = () => {
    const randomNumber = (min = 1, max = 100000) =>
      Math.floor(Math.random() * (max - min + 1)) + min;

    return {
      compulsory: Math.random() > 0.5 ? 'Yes' : 'Not applicable',
      probability: randomNumber(1, 5),

      notionalAmount: randomNumber(1, 1000),
      swapTenor: randomNumber(1, 5),
      paymentFrequency: randomNumber(1, 5),
      discountRate: randomNumber(1, 5),
      forwardRate: randomNumber(1, 5),

      expectation: randomNumber(1, 5).toString(),
    };
  };

  return (
    <FormProvider {...formMethods}>
      <div className={styles.container}>
        <MainQuestion
          id="interest"
          title={InterestData.title}
          description={InterestData.description}
          compulsoryQuestions={InterestData.compulsoryQuestions}
          probabilityQuestions={InterestData.probabilityQuestions}
        />

        {interest?.compulsory && interest?.compulsory == 'Yes' && (
          <SectionContainer>
            <Title>Interest Rate Sensitivity Analysis</Title>

            <InputContainer>
              <FormInput
                id="interest.notionalAmount"
                label="Notional amount"
                placeholder="Fill in notional amount"
                type="number"
                required
              />

              <FormInput
                id="interest.swapTenor"
                label="Swap tenor (years)"
                placeholder="Fill in swap tenor (years)"
                type="number"
                required
              />

              <FormInput
                id="interest.paymentFrequency"
                label="Payment frequency"
                placeholder="Fill in payment frequency"
                type="number"
                required
              />

              <FormInput
                id="interest.discountRate"
                label="Discount Rate"
                placeholder="Fill in discount rate"
                type="number"
                required
              />

              <FormInput
                id="interest.forwardRate"
                label="Forward rate (% per period, comma separated) "
                placeholder="Fill in forward rate (% per period, comma separated) "
                type="number"
                required
              />

              <div></div>

              <Button>Calculate swap rate</Button>
            </InputContainer>

            <InputContainer>
              <FormDropdown
                id="interest.expectation"
                label="Is the result within your expectation?"
                placeholder="Choose the expectation"
                options={expectationOptions || []}
                required
              />
            </InputContainer>
          </SectionContainer>
        )}
      </div>

      <StepperNavigation
        isValid={isValid}
        onPrev={() => onHandleNavigation('prev')}
        onNext={() => onHandleNavigation('next')}
      />

      <Portal root="tester">
        <Button
          isCircle
          onClick={() => {
            reset({
              ...getValues(),
              interest: {
                ...getValues().interest,
                ...generateRandomData(),
              },
            });
          }}
        >
          Random
        </Button>
      </Portal>
    </FormProvider>
  );
};

export default Interest;
